import { RealRewardStep } from "@hg/activity-utils"
import Modal from "@hg/modal"
import cls from "classnames"
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"

import OutlineButton from "@/components/OutlineButton"
import Toast from "@/components/Toast"
import { i18n } from "@/i18n"
import { requestPostContact } from "@/services/activity"
import { Code2Msg } from "@/Topics/Recall/helper"

import RewardWrapper from "../RewardWrapper"
import styles from "./index.module.scss"

import type { ContactInfo } from "@/services/activity"

const AddressContainer = Modal.createContainer("__ADDRESS_CONTAINER")
AddressContainer.rootElem.style.zIndex = "999"

export interface EditAddressModalProps {
    open: boolean
    onClose: () => void
    contactInfo?: ContactInfo | null | undefined
    customTitle?: React.ReactNode
    step?: RealRewardStep
    loading?: boolean
    coupon?: string
    onContactChange?: (contact: ContactInfo) => void
    onStepChange?: (step: RealRewardStep) => void
    onSubmitContact?: () => void
    onRefresh?: () => void
    noModal?: boolean
    useTempAddress?: boolean
}

// ─── 表单状态 ─────────────────────────────────────────────────────────────────

type FormState = {
    name: string
    phone: string
    email: string
    postcode: string
    address1: string // 住所1：郵便番号から自動取得、編集不可
    address2: string // 住所2：番地・建物名等
}

const EMPTY_FORM: FormState = {
    name: "",
    phone: "",
    email: "",
    postcode: "",
    address1: "",
    address2: "",
}

function contactInfoToForm(ci?: ContactInfo | null): FormState {
    if (!ci) return EMPTY_FORM
    return {
        name: ci.name ?? "",
        phone: ci.phone ?? "",
        email: ci.email ?? "",
        postcode: ci.postcode ?? "",
        address1: ci.province ?? "",
        address2: ci.address ?? "",
    }
}

function formToContactInfo(form: FormState): ContactInfo {
    return {
        name: form.name,
        phone: form.phone,
        email: form.email,
        postcode: form.postcode,
        province: form.address1,
        address: form.address2,
        city: "",
        county: "",
    }
}

// ─── バリデーション ────────────────────────────────────────────────────────────

function validateForm(form: FormState): string | null {
    if (!form.name.trim()) return i18n.get("reward.error_name_empty")
    if (!form.phone) return i18n.get("reward.error_phone_empty")
    if (!/^\d{10,11}$/.test(form.phone)) return i18n.get("reward.error_phone_format")
    if (!form.email) return i18n.get("reward.error_email_empty")
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return i18n.get("reward.error_email_format")
    if (!form.postcode) return i18n.get("reward.error_postcode_empty")
    if (!/^\d{7}$/.test(form.postcode)) return i18n.get("reward.error_postcode_format")
    if (!form.address1) return i18n.get("reward.error_zipcode_fail")
    if (!form.address2.trim()) return i18n.get("reward.error_address_empty")
    return null
}

// ─── 郵便番号 → 住所検索 ──────────────────────────────────────────────────────

async function fetchAddress(postcode: string): Promise<string | null> {
    try {
        const res = await fetch(
            `https://api.zipaddress.net/?zipcode=${postcode}`,
        )
        const json = await res.json()
        if (json.code === 200 && json.data?.fullAddress) {
            return json.data.fullAddress as string
        }
        return null
    } catch {
        return null
    }
}

// ─── コンポーネント ────────────────────────────────────────────────────────────

export const EditAddressModal: FC<EditAddressModalProps> = ({
    open,
    onClose,
    contactInfo,
    customTitle,
    step: originalStep,
    loading: originalLoading,
    coupon,
    onContactChange,
    onStepChange,
    onSubmitContact,
    onRefresh,
    noModal,
}) => {
    const [step, setStep] = useState<RealRewardStep>(
        originalStep ||
            (contactInfo ? RealRewardStep.PREVIEW : RealRewardStep.EDIT),
    )
    const [form, setForm] = useState<FormState>(contactInfoToForm(contactInfo))
    const [loading, setLoading] = useState(originalLoading)
    const [zipLoading, setZipLoading] = useState(false)

    const handleStepChange = useCallback(
        (s: RealRewardStep) => {
            setStep(s)
            onStepChange?.(s)
        },
        [onStepChange],
    )

    // 同步外部状态
    useEffect(() => {
        if (originalStep !== undefined) setStep(originalStep)
        if (contactInfo) {
            setForm(contactInfoToForm(contactInfo))
            if (!originalStep) setStep(RealRewardStep.PREVIEW)
        }
        if (typeof originalLoading === "boolean") setLoading(originalLoading)
    }, [originalStep, contactInfo, originalLoading])

    // 关闭时重置
    useEffect(() => {
        if (!open) {
            handleStepChange(
                contactInfo ? RealRewardStep.PREVIEW : RealRewardStep.EDIT,
            )
        }
    }, [open, contactInfo, handleStepChange])

    const updateField = (key: keyof FormState, value: string) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value }
            onContactChange?.(formToContactInfo(next))
            return next
        })
    }

    // 電話番号は数字のみ・最大11桁
    const handlePhoneChange = (value: string) => {
        const digits = value.replace(/\D/g, "").slice(0, 11)
        updateField("phone", digits)
    }

    // 郵便番号が7桁になったら住所を自動取得
    const handlePostcodeChange = async (value: string) => {
        const digits = value.replace(/\D/g, "").slice(0, 7)
        updateField("postcode", digits)
        updateField("address1", "")
        if (digits.length === 7) {
            setZipLoading(true)
            const addr = await fetchAddress(digits)
            setZipLoading(false)
            if (addr) {
                setForm((prev) => {
                    const next = { ...prev, address1: addr }
                    onContactChange?.(formToContactInfo(next))
                    return next
                })
            } else {
                Toast.message(i18n.get("reward.error_zipcode_fail"))
            }
        }
    }

    const handleSubmitContact = async () => {
        const err = validateForm(form)
        if (err) {
            Toast.message(err)
            return
        }
        if (onSubmitContact) {
            onSubmitContact()
            return
        }
        setLoading(true)
        try {
            const res = await requestPostContact(coupon ?? "", formToContactInfo(form))
            if (res.code === 0) {
                onRefresh?.()
                handleStepChange(RealRewardStep.DONE)
            } else {
                Toast.message(Code2Msg(res.code) || res.message)
            }
        } catch (error: unknown) {
            Toast.message(
                error instanceof Error ? error.message : String(error) || "-",
            )
        } finally {
            setLoading(false)
        }
    }

    const modalTip = useMemo(
        () =>
            !contactInfo
                ? i18n.getRecall("award.edit_notice")
                : i18n.getRecall("award.show_notice"),
        [contactInfo],
    )

    // ─── フィールド定義 ────────────────────────────────────────────────────────

    const FIELDS: {
        key: keyof FormState
        label: string
        readOnly?: boolean
        placeholder?: string
    }[] = [
        {
            key: "name",
            label: i18n.get("reward.placeholder_name"),
        },
        {
            key: "phone",
            label: i18n.get("reward.placeholder_phone"),
        },
        {
            key: "email",
            label: i18n.get("reward.placeholder_email"),
        },
        {
            key: "postcode",
            label: i18n.get("reward.placeholder_postcode"),
        },
        {
            key: "address1",
            label: i18n.get("reward.placeholder_address1"),
            readOnly: true,
            placeholder: zipLoading
                ? i18n.get("reward.address1_loading")
                : i18n.get("reward.placeholder_address1"),
        },
        {
            key: "address2",
            label: i18n.get("reward.placeholder_address2"),
            placeholder: i18n.get("reward.placeholder_address2_hint"),
        },
    ]

    const content = (
        <RewardWrapper onClose={onClose} showRuleButton={true} showTitle>
            <div className={cls(styles.main, styles.fadeContent)}>
                <div className={styles.prize}>
                    {step === RealRewardStep.EDIT && (
                        <>
                            {customTitle ? (
                                customTitle
                            ) : (
                                <div className={styles.title}>{modalTip}</div>
                            )}
                            <div className={styles.addList}>
                                {FIELDS.map(
                                    ({ key, label, readOnly, placeholder }) => (
                                        <div
                                            key={key}
                                            className={styles.inputGroup}
                                        >
                                            <div className={styles.label}>
                                                {label}
                                            </div>
                                            <input
                                                className={cls(
                                                    styles.fieldInput,
                                                    readOnly &&
                                                        styles.readOnly,
                                                )}
                                                value={form[key]}
                                                readOnly={readOnly}
                                                placeholder={placeholder ?? label}
                                                onChange={
                                                    key === "postcode"
                                                        ? (e) =>
                                                              handlePostcodeChange(
                                                                  e.target.value,
                                                              )
                                                        : key === "phone"
                                                          ? (e) =>
                                                                handlePhoneChange(
                                                                    e.target
                                                                        .value,
                                                                )
                                                          : readOnly
                                                            ? undefined
                                                            : (e) =>
                                                                  updateField(
                                                                      key,
                                                                      e.target
                                                                          .value,
                                                                  )
                                                }
                                            />
                                        </div>
                                    ),
                                )}
                            </div>
                            <div className={styles.btnGroup}>
                                <OutlineButton
                                    className={styles.btn}
                                    text={i18n.getRecall("poster.button_next")}
                                    onClick={() => {
                                        const err = validateForm(form)
                                        if (err) {
                                            Toast.message(err)
                                            return
                                        }
                                        handleStepChange(RealRewardStep.CONFIRM)
                                    }}
                                    fontSize={34}
                                    strokeWidth={10}
                                    strokeColor="#872D19"
                                    fillGradient={{
                                        from: "#FFFAF6",
                                        to: "#FCF1D2",
                                    }}
                                />
                            </div>
                        </>
                    )}

                    {step === RealRewardStep.CONFIRM && (
                        <>
                            <div
                                className={styles.addressPreview}
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: i18n
                                dangerouslySetInnerHTML={{
                                    __html: i18n.getRecall(
                                        "award.address_preview_notice",
                                    ),
                                }}
                            />
                            <div className={styles.addList}>
                                {FIELDS.map(({ key, label }) => (
                                    <div
                                        key={key}
                                        className={styles.inputGroup}
                                    >
                                        <div className={styles.label}>
                                            {label}
                                        </div>
                                        <div className={styles.text}>
                                            {form[key]}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.btnGroup}>
                                <OutlineButton
                                    className={cls(styles.btn, styles.return)}
                                    text={i18n.get("reward.button_return")}
                                    onClick={() =>
                                        handleStepChange(RealRewardStep.EDIT)
                                    }
                                    fontSize={34}
                                    strokeWidth={10}
                                    strokeColor="#1F5A5F"
                                />
                                <OutlineButton
                                    className={styles.btn}
                                    text={i18n.get("reward.button_confirm")}
                                    onClick={handleSubmitContact}
                                    disabled={loading}
                                    fontSize={34}
                                    strokeWidth={10}
                                    strokeColor="#872D19"
                                />
                            </div>
                        </>
                    )}

                    {step === RealRewardStep.DONE && (
                        <div className={styles.done}>
                            <div
                                className={styles.intro}
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: i18n
                                dangerouslySetInnerHTML={{
                                    __html: i18n.get("reward.text_introduction"),
                                }}
                            />
                        </div>
                    )}

                    {step === RealRewardStep.PREVIEW && (
                        <>
                            <div
                                className={styles.addressPreview}
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: i18n
                                dangerouslySetInnerHTML={{
                                    __html: i18n.getRecall(
                                        "award.address_preview_notice",
                                    ),
                                }}
                            />
                            <div className={styles.addList}>
                                {FIELDS.map(({ key, label }) => (
                                    <div
                                        key={key}
                                        className={styles.inputGroup}
                                    >
                                        <div className={styles.label}>
                                            {label}
                                        </div>
                                        <div className={styles.text}>
                                            {form[key]}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </RewardWrapper>
    )

    return noModal ? (
        content
    ) : (
        <Modal
            active={open}
            onClose={onClose}
            maskClosable
            contentClassName={styles.addressModal}
            container={AddressContainer}
        >
            {content}
        </Modal>
    )
}
